import { useEffect, useState } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";

export function SuggestionsDialog({ suggestions, open, onClose, onDone }) {
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) setChecked(Object.fromEntries(suggestions.map((s) => [s.tx_id, true])));
  }, [open, suggestions]);

  const n = Object.values(checked).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const groups = {};
      suggestions.filter((s) => checked[s.tx_id]).forEach((s) => {
        const key = `${s.kind}|${s.category_id || ""}|${s.branch_id || ""}`;
        (groups[key] = groups[key] || { ...s, tx_ids: [] }).tx_ids.push(s.tx_id);
      });
      let ok = 0, errs = 0;
      for (const g of Object.values(groups)) {
        const r = await api.post("/reconciliation/categorize-bulk", {
          tx_ids: g.tx_ids, kind: g.kind, category_id: g.category_id || undefined,
          branch_id: g.branch_id || undefined,
        });
        ok += r.data.ok;
        errs += r.data.errors?.length || 0;
      }
      toast[errs ? "warning" : "success"](`${ok} categorizados${errs ? ` · ${errs} con error` : ""}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar sugerencias");
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="suggestions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Sugerencias automáticas</DialogTitle>
          <DialogDescription>Reglas por concepto: TPV por código de comercio y palabras clave (comisiones, Seguridad Social, nóminas, préstamos, impuestos).</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="sug-toggle-all" className="accent-[#D9381E]"
              checked={suggestions.length > 0 && n === suggestions.length}
              onChange={() => setChecked(Object.fromEntries(suggestions.map((s) => [s.tx_id, n !== suggestions.length])))} />
            Seleccionar todas
          </label>
          <button type="button" data-testid="sug-select-none" className="underline hover:text-slate-700" onClick={() => setChecked({})}>Ninguna</button>
        </div>
        <div className="space-y-1.5 min-w-0 overflow-y-auto flex-1 min-h-0">
          {suggestions.map((s) => (
            <div key={s.tx_id} data-testid={`sug-row-${s.tx_id}`}
              className="flex items-center gap-3 border rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50"
              onClick={() => setChecked((p) => ({ ...p, [s.tx_id]: !p[s.tx_id] }))}>
              <input type="checkbox" readOnly checked={!!checked[s.tx_id]} className="accent-[#D9381E]" />
              <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(s.date)}</span>
              <span className="flex-1 min-w-0 text-sm truncate" title={s.concept}>{shortConcept(s.concept)}</span>
              <span className={`font-num text-sm font-semibold whitespace-nowrap ${s.amount >= 0 ? "text-green-700" : "text-red-700"}`}>{fmtEUR(s.amount)}</span>
              <Badge variant="outline" className={s.kind === "liquidacion_tarjeta" ? "text-blue-700 border-blue-200 bg-blue-50" : "text-amber-700 border-amber-200 bg-amber-50"}>
                {s.kind === "liquidacion_tarjeta" ? `TPV ${s.branch_name}` : s.category_name}
              </Badge>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-sm text-slate-500">{n} de {suggestions.length} seleccionadas</span>
          <Button data-testid="sug-apply-button" onClick={apply} disabled={!n || applying}
            className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
            <CheckCheck className="w-4 h-4" /> {applying ? "Aplicando..." : `Aplicar ${n} sugerencias`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
